import { Form, redirect, useActionData, useNavigation } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { errorMessage, loadSession } from "@/lib/session";
import { useSession } from "@/routes/root";
import { tenantPath } from "@/lib/tenants";
import { INVITATION_ACCEPTED, INVITATION_DECLINED } from "@/lib/invitations";
import { toast } from "@/lib/toast";
import { PageTitle } from "@/components/brand";
import { SpacePicture } from "@/components/avatar";
import { ErrorNote } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * The page an emailed invitation link lands on.
 *
 * It renders a choice and never acts on its own. A GET must not join anybody
 * to anything: mail clients and security scanners follow links, and a link
 * that enrols you on being opened is a link somebody else can trigger.
 *
 * Signed out, it sends you to sign in and comes back — the token stays in the
 * URL through the round trip, and acceptance is still bound to the address the
 * invitation names, so signing in as the wrong account does not redeem it.
 */

export async function invitationLoader({ request }: LoaderFunctionArgs) {
  const session = await loadSession();
  if (!session) {
    const next = new URL(request.url).pathname;
    throw redirect(`/sign-in?next=${encodeURIComponent(next)}`);
  }
  await read(queries.invitations());
  return null;
}

type ActionResult = { error?: string };

export async function invitationAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  const token = params.token ?? "";

  try {
    if (String(form.get("intent")) === "decline") {
      await api.invitations.decline({ token });
      toast(INVITATION_DECLINED);
      return redirect("/spaces");
    }
    const joined = await api.invitations.accept({ token });
    toast(INVITATION_ACCEPTED);
    return redirect(tenantPath(joined));
  } catch (error) {
    return { error: errorMessage(error, "That invitation is not valid") };
  }
}

export default function InvitationPage() {
  const session = useSession();
  // The list is filtered to invitations addressed to this account, so an
  // invitation that is not here is one this account may not accept — whether
  // because it is somebody else's, or because it has expired.
  const invitation = useSuspenseQuery(queries.invitations()).data.invitations[0] ?? null;
  const result = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const busy = navigation.state === "submitting";

  return (
    <section className="mx-auto max-w-md space-y-6">
      <PageTitle of={["You have been invited"]} />
      <h1 className="page-title">You have been invited</h1>

      <Card>
        <CardContent className="space-y-5">
          {invitation ? (
            <div className="flex items-center gap-4">
              <SpacePicture space={invitation.tenant} size="xl" className="shrink-0" />
              <p className="min-w-0 text-muted-foreground wrap-anywhere">
                <strong className="font-medium text-foreground">{invitation.tenant.name}</strong> invited you to join
                as {invitation.role}.
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground">
              This invitation is not valid for {session?.user.email}. It may have
              expired, already been used, or been sent to a different address.
            </p>
          )}

          {result?.error && <ErrorNote message={result.error} />}

          {/* Two buttons, no default: joining is always something chosen. */}
          <div className="grid grid-cols-2 gap-2">
            <Form method="post">
              <Button type="submit" size="lg" disabled={busy} className="w-full">
                Accept
              </Button>
            </Form>
            <Form method="post">
              <input type="hidden" name="intent" value="decline" />
              <Button type="submit" variant="outline" size="lg" disabled={busy} className="w-full">
                Decline
              </Button>
            </Form>
          </div>
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">
        Signed in as {session?.user.email}. An invitation is tied to the address
        it was sent to, so accepting while signed in as someone else will not
        work.
      </p>
    </section>
  );
}
